/**
 * Shared fields (amount, TLD, schedule, static tags, drop options) for multi-country edit.
 */

import { Route } from 'lucide-react';

import { Input, SearchableSelect } from '@/components/ui';
import { DropOptionsSection, StaticTagsPickerSection } from '@/features/smswiser-campaigns/components/drop-modals';

import { SharedScheduleBlock } from './schedule';
import type { useMultiCountryBulkEditForm } from '../hooks';

type Form = ReturnType<typeof useMultiCountryBulkEditForm>;

interface Props {
  form: Form;
  tlds: Array<{ value: string; displayName: string }>;
  isBusy: boolean;
}

export function SharedFieldsSection({ form, tlds, isBusy }: Props) {
  return (
    <div className="space-y-4">
      <section className="space-y-3">
        <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <Route className="h-3.5 w-3.5" /> Route & Amount
        </h4>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">TLD (leave blank unchanged)</label>
            <SearchableSelect<string>
              options={[
                { value: '', label: '— Leave unchanged —' },
                ...tlds.map((t) => ({ value: t.value, label: t.displayName })),
              ]}
              value={form.tldValue || ''}
              onChange={form.setTldValue}
              placeholder="Select TLD"
              searchPlaceholder="Search TLDs..."
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Amount (leave blank unchanged)</label>
            <Input
              type="number"
              min={1}
              placeholder="Leave unchanged"
              className="h-10"
              value={form.amount === '' ? '' : form.amount}
              onChange={(e) => form.setAmount(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>
        </div>
        <SharedScheduleBlock form={form} isBusy={isBusy} />
      </section>

      <section className="space-y-3">
        <DropOptionsSection
          hideTitle compact allowUnchanged enableBatchesAndAi
          amount={form.amount}
          forceSend={form.forceSend}
          setForceSend={form.setForceSend}
          sendInBatches={form.batchAmount != null ? true : 'unchanged'}
          setSendInBatches={(v) => {
            if (v === true) { form.setBatchAmount(0); form.setBatchDelay(0); }
            else { form.setBatchAmount(null); form.setBatchDelay(null); }
          }}
          batchAmount={form.batchAmount ?? undefined}
          setBatchAmount={(v) => form.setBatchAmount(v ?? null)}
          batchDelay={form.batchDelay ?? undefined}
          setBatchDelay={(v) => form.setBatchDelay(v ?? null)}
          aiOptimizationEnabled={form.aiOptEnabled}
          setAiOptimizationEnabled={form.setAiOptEnabled}
          aiOptimizationType={form.aiOptType ?? ''}
          setAiOptimizationType={form.setAiOptType}
          aiOptimizationBatchAmount={form.aiOptBatch ?? ''}
          setAiOptimizationBatchAmount={(v) => form.setAiOptBatch(v === '' ? null : v)}
          aiOptimizationMinimalValuePercent={form.aiOptMinPercent ?? ''}
          setAiOptimizationMinimalValuePercent={(v) => form.setAiOptMinPercent(v === '' ? null : v)}
          aiOptimizationSendUntil={form.aiOptSendUntil ?? ''}
          setAiOptimizationSendUntil={(v) => form.setAiOptSendUntil(v || null)}
          useOnlyNewDomains={form.useOnlyNewDomains}
          setUseOnlyNewDomains={form.setUseOnlyNewDomains}
          shortlink={form.shortlink}
          isBusy={isBusy}
        />
      </section>

      <section className="space-y-2">
        <StaticTagsPickerSection
          mode="single"
          selectedId={form.staticTagId}
          onChange={(id) => form.setStaticTagId(id)}
          allowUnchanged compact
          disabled={isBusy}
        />
      </section>
    </div>
  );
}
